/**
 * @fileoverview CSRFトークンプロバイダーの実装
 *
 * このファイルでは、CSRFトークンを管理するためのContextプロバイダーコンポーネントを
 * 実装しています。APIからのトークン取得、トークンの保存、エラー処理などの
 * 機能を提供し、アプリケーション全体でCSRFトークンを利用可能にします。
 */

import { ReactNode, FC, useState, useCallback, useEffect } from "react";
import {
  type CsrfTokenType,
  type CsrfTokenContextType,
  CsrfTokenContext,
} from "./context";
import * as api from "@/api/issueCsrfToken";

/**
 * CSRFトークンコンテキストのプロバイダーコンポーネント
 *
 * セッションストレージからトークンを初期化し、必要に応じてAPIから新しいトークンを
 * 取得します。トークンの状態、ローディング状態、エラー状態を管理し、
 * これらの情報をコンテキストを通じて子コンポーネントに提供します。
 *
 * @component
 * @param {Object} props - コンポーネントのプロパティ
 * @param {ReactNode} props.children - プロバイダーの子コンポーネント
 * @returns {JSX.Element} CSRFトークンコンテキストプロバイダー
 */
export const CsrfTokenContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState<CsrfTokenType>(
    sessionStorage.getItem("csrfToken") ?? undefined
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const issueCsrfToken = useCallback(async () => {
    setLoading(true);
    try {
      const token = await api.issueCsrfToken();
      setCsrfToken(token);
      window.sessionStorage.setItem("csrfToken", token);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(`Unknown error: ${e}`);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!csrfToken) {
      issueCsrfToken();
    }
  }, [csrfToken, issueCsrfToken]);

  const defaultValue = {
    csrfToken,
    issueCsrfToken,
    loading,
    error,
  } satisfies CsrfTokenContextType;

  return (
    <CsrfTokenContext.Provider value={defaultValue}>
      {children}
    </CsrfTokenContext.Provider>
  );
};
